import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import Template from './template';
import { DocReview } from 'components';
import { apiGetDocRejectInfo, apiDocReview } from 'api/ZimssaAPI';
import { PartnerAuthState } from 'stores/PartnerAuthStore';
import { useApiRequest, useServiceStatus } from 'hooks';
import { SERVICE_SELECT } from 'router/PageUrl';
import ZimssaConstant from 'commons/ZimssaConstant';

function DocReviewView(): React.ReactElement {
  const history = useHistory();
  const { partnerIdx } = useContext(PartnerAuthState);
  const [docReviewState, setDocReviewState] =
    useState<IDocReviewState>(defaultState);
  const [docReviewForm, setDocReviewForm] =
    useState<IDocReviewForm>(defaultForm);
  const { serviceIdx, serviceType } = useServiceStatus();
  const [fetchResult, fetchRejectInfo] =
    useApiRequest<any>(apiGetDocRejectInfo);

  const serviceImgUploadDir =
    serviceType === 'familymove'
      ? ZimssaConstant.IMG_DIR.SERVICE.FAMILYMOVE.replace(
          '{{partnerIdx}}',
          `${partnerIdx}`
        )
      : ZimssaConstant.IMG_DIR.SERVICE.SMALLMOVE.replace(
          '{{partnerIdx}}',
          `${partnerIdx}`
        );

  useEffect(() => {
    if (serviceIdx && serviceType) {
      fetchRejectInfo({
        type: 'REQUEST',
        body: [serviceIdx, serviceType],
      });
    }
  }, [fetchRejectInfo, serviceIdx, serviceType]);

  useEffect(() => {
    const { result, data } = fetchResult;
    if (result === 'success') {
      const responseData: {
        serviceImageValid?: boolean;
        introduceValid?: boolean;
        businessRegistrationImageValid?: boolean;
        familyRelationImageValid?: boolean;
        transportationLicenseImageValid?: boolean;
        indemnityInsuranceImageValid?: boolean;
        arrangeLicenseImageValid?: boolean;
        carImageValid?: boolean;
      } = data;

      setDocReviewState({
        serviceImageValid:
          responseData.serviceImageValid === null
            ? true
            : responseData.serviceImageValid,
        introduceValid:
          responseData.introduceValid === null
            ? true
            : responseData.introduceValid,
        businessRegistrationImageValid:
          responseData.businessRegistrationImageValid === null
            ? true
            : responseData.businessRegistrationImageValid,
        familyRelationImageValid:
          responseData.familyRelationImageValid === null
            ? true
            : responseData.familyRelationImageValid,
        transportationLicenseImageValid:
          responseData.transportationLicenseImageValid === null
            ? true
            : responseData.transportationLicenseImageValid,
        indemnityInsuranceImageValid:
          responseData.indemnityInsuranceImageValid === null
            ? true
            : responseData.indemnityInsuranceImageValid,
        arrangeLicenseImageValid:
          responseData.arrangeLicenseImageValid === null
            ? true
            : responseData.arrangeLicenseImageValid,
        carImageValid:
          responseData.carImageValid === null
            ? true
            : responseData.carImageValid,
      });
    }
  }, [fetchResult]);

  const docReviewProps = {
    serviceImage: docReviewState.serviceImageValid
      ? undefined
      : {
          inputName: 'serviceImage',
          imgDir: serviceImgUploadDir,
          isServiceLogo: true,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              serviceImage: value,
            });
          },
        },
    introduce: docReviewState.introduceValid
      ? undefined
      : {
          inputName: 'introduce',
          onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const { value } = e.target;
            setDocReviewForm({
              ...docReviewForm,
              introduce: value,
            });
          },
        },
    businessRegistrationImage: docReviewState.businessRegistrationImageValid
      ? undefined
      : {
          inputName: 'businessRegistrationImage',
          imgDir: serviceImgUploadDir,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              businessRegistrationImage: value,
            });
          },
        },
    familyRelationImage: docReviewState.familyRelationImageValid
      ? undefined
      : {
          inputName: 'familyRelationImage',
          imgDir: serviceImgUploadDir,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              familyRelationImage: value,
            });
          },
        },
    transportationLicenseImage: docReviewState.transportationLicenseImageValid
      ? undefined
      : {
          inputName: 'transportationLicenseImage',
          imgDir: serviceImgUploadDir,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              transportationLicenseImage: value,
            });
          },
        },
    arrangeLicenseImage: docReviewState.arrangeLicenseImageValid
      ? undefined
      : {
          inputName: 'arrangeLicenseImage',
          imgDir: serviceImgUploadDir,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              arrangeLicenseImage: value,
            });
          },
        },
    indemnityInsuranceImage: docReviewState.indemnityInsuranceImageValid
      ? undefined
      : {
          inputName: 'indemnityInsuranceImage',
          imgDir: serviceImgUploadDir,
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              indemnityInsuranceImage: value,
            });
          },
        },
    carImage: docReviewState.carImageValid
      ? undefined
      : {
          inputName: 'carImage',
          imgDir: ZimssaConstant.IMG_DIR.SERVICE.SMALLMOVE_CAR.replace(
            '{{partnerIdx}}',
            `${partnerIdx}`
          ),
          isDeleteBtn: true,
          onChangeHandler: (type: any, value: string) => {
            setDocReviewForm({
              ...docReviewForm,
              carImage: value,
            });
          },
        },
    btnNext: {
      contents: '완료',
      isActive: (() => {
        const validStates: { [key: string]: any } = docReviewState;
        const validForms: { [key: string]: any } = docReviewForm;
        const validKeys = Object.keys(validStates)
          .filter((key) => validStates[key] === false)
          .map((key) => key.replace('Valid', ''));

        return Object.keys(validForms)
          .filter((key) => validKeys.indexOf(key) > -1 && key !== 'introduce')
          .every((key) => validForms[key]);
      })(),
      isComplete: false,
      onClick: useCallback(
        (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
          apiDocReview({
            serviceIdx,
            serviceType,
            ...docReviewForm,
          })
            .then((response) => {
              const { result, message } = response;
              if (result === 'success') {
                alert('서류를 재등록 하였습니다.');
                history.push(SERVICE_SELECT.url);
              } else {
                if (message) alert(message);
              }
            })
            .catch((error) => {});
        },
        [docReviewForm, history, serviceIdx, serviceType]
      ),
    },
  };

  return (
    <Template>
      <DocReview {...docReviewProps} />
    </Template>
  );
}

export default DocReviewView;

const defaultState: IDocReviewState = {
  serviceImageValid: true,
  introduceValid: true,
  businessRegistrationImageValid: true,
  familyRelationImageValid: true,
  transportationLicenseImageValid: true,
  indemnityInsuranceImageValid: true,
  arrangeLicenseImageValid: true,
  carImageValid: true,
};

const defaultForm: IDocReviewForm = {
  serviceImage: '',
  introduce: '',
  businessRegistrationImage: '',
  familyRelationImage: '',
  transportationLicenseImage: '',
  indemnityInsuranceImage: '',
  arrangeLicenseImage: '',
  carImage: '',
};

interface IDocReviewState {
  serviceImageValid?: boolean;
  introduceValid?: boolean;
  businessRegistrationImageValid?: boolean;
  familyRelationImageValid?: boolean;
  transportationLicenseImageValid?: boolean;
  indemnityInsuranceImageValid?: boolean;
  arrangeLicenseImageValid?: boolean;
  carImageValid?: boolean;
}

interface IDocReviewForm {
  serviceImage: string;
  introduce: string;
  businessRegistrationImage: string;
  familyRelationImage: string;
  transportationLicenseImage: string;
  indemnityInsuranceImage: string;
  arrangeLicenseImage: string;
  carImage: string;
}
